import React, { useState, useEffect, useContext } from 'react';
import Image from 'next/future/image';
import cn from 'classnames';
import { DropdownContext } from './DropdownContext';
import downArrow from '@/public/icons/smartForm/downArrow.svg';

type Props = {
  labelTitle: string;
  options: { optionLabel: string; value: string }[];
  defaultOptionLabel: string;
  defaultValue: string;
};

export default function Dropdown(props: {
  config: Props;
  callback: (value: string) => void;
}) {
  const { config, callback } = props;
  const [isMenuReadable, toggleDisplay] = useState(false);
  const [selected, setSelected] = useState(config.defaultValue);
  const currentSelected = useContext(DropdownContext);

  const [currentOptionLabel, setCurrentOptionLabel] = useState(
    config.defaultOptionLabel,
  );

  const onClickToggleDisplay = () => {
    toggleDisplay(!isMenuReadable);
  };

  const onChangeSelect = (selection: string, optionLabel: string) => {
    setSelected(selection);
    setCurrentOptionLabel(optionLabel);
    callback(selection);
    toggleDisplay(false);
  };

  useEffect(() => {
    if (currentSelected === '' && selected !== '') {
      setSelected(config.defaultValue);
      setCurrentOptionLabel(config.defaultOptionLabel);
      callback('');
      toggleDisplay(false);
    }
  }, [currentSelected]);

  return (
    <>
      <div className="relative w-full text-left">
        <h5 className="mb-2 block font-semibold text-gray-700 lg:text-base">
          {config.labelTitle}
        </h5>
        <div className="w-full">
          <button
            type="button"
            className="inline-flex h-12 w-full justify-between rounded-sm border border-solid border-gray-500 bg-white py-3 pr-3 pl-2 font-medium leading-normal text-gray-700 outline-1 outline-gray-700 lg:pr-6 lg:pl-4"
            id="menu-button"
            aria-expanded="true"
            aria-haspopup="true"
            onClick={onClickToggleDisplay}
          >
            <span
              className={cn(
                'my-auto truncate pl-2 text-xs text-gray-700 sm:pl-0 sm:text-sm sm:text-gray-700',
                {
                  'text-gray-700/40':
                    currentOptionLabel === config.defaultOptionLabel,
                },
              )}
            >
              {currentOptionLabel}
            </span>
            <span className="my-auto h-[0.65rem] w-[0.65rem] pt-[0.15rem] sm:h-[1rem] sm:w-[1rem] sm:pt-[0.25rem]">
              <Image
                src={downArrow}
                alt="Dropdown menu icon"
                className="w-full object-contain"
              />
            </span>
          </button>
        </div>
        <div
          className="relative z-10 -mt-12 w-full origin-top-right rounded-sm border-gray-300 pt-1 focus:outline-none sm:absolute sm:border sm:border-solid sm:bg-white sm:pt-0 sm:shadow-xl"
          hidden={!isMenuReadable}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
          tabIndex={-1}
        >
          <div
            className="-mt-3 sm:mt-0"
            role="none"
            onMouseLeave={() => toggleDisplay(false)}
          >
            {config.options.map((option, idx) => {
              const { value, optionLabel } = option;

              return (
                <a
                  key={idx}
                  className={cn(
                    'sm:hover:font-bolds mt-2 block cursor-pointer truncate rounded border border-solid bg-white py-4 px-4 text-xs text-gray-700 sm:mt-0 sm:border-white sm:py-2 sm:text-sm sm:hover:bg-gray-300',
                    {
                      'border-2 border-solid border-gray-700 font-bold sm:border-gray-700':
                        value === selected,
                    },
                  )}
                  role="menuitem"
                  tabIndex={-1}
                  id="menu-item-0"
                  onClick={() => onChangeSelect(value, optionLabel)}
                >
                  {optionLabel}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
